import { Badge, Group, Title, Tooltip } from "@mantine/core"
import { IconSparkles } from "@tabler/icons-react"
import { useMemo } from "react"

import dayjs from "@costory/shared/dayjs"
import { calculateRelativeChange } from "@costory/shared/utils/format"

import { ChangeIndicator } from "@costory/front/components/ChangeIndicator"

interface DigestHeaderProps {
  data: {
    costCurrent: number
    costPrevious: number
  }
  period: string
  fullAxesPath: string
  lastAxis: string
}

// Simple dot separator component
const Dot = () => (
  <span
    style={{
      width: 5,
      height: 5,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    }}
  />
)

export const DigestHeader = ({
  data: { costCurrent, costPrevious },
  period,
  fullAxesPath,
  lastAxis,
}: DigestHeaderProps) => {
  // Calculate total change
  const totalChange = useMemo(
    () =>
      calculateRelativeChange(
        costCurrent - costPrevious,
        costPrevious,
        costCurrent,
      ),
    [costCurrent, costPrevious],
  )

  // Calculate average change
  const avgChange = useMemo(() => {
    const current = dayjs(period)
    const previous = current.subtract(1, "month")
    const avgCurrent = costCurrent / current.daysInMonth()
    const avgPrevious = costPrevious / previous.daysInMonth()

    return calculateRelativeChange(
      avgCurrent - avgPrevious,
      avgPrevious,
      avgCurrent,
    )
  }, [costCurrent, costPrevious, period])

  return (
    <Group align="center" gap="md">
      <Tooltip
        label={fullAxesPath}
        multiline
        maw="60%"
        data-intercom-target="costory-tour-digest-header-axis-path"
      >
        <Title order={2} fw={700}>
          {lastAxis}
        </Title>
      </Tooltip>

      <Dot />

      <ChangeIndicator changeDetails={totalChange} />

      <Dot />

      <ChangeIndicator changeDetails={avgChange} label="/day" />

      <Badge
        leftSection={<IconSparkles size={14} />}
        color="violet"
        radius="xl"
        variant="light"
        ml="md"
        data-intercom-target="costory-tour-digest-header-creator-badge"
      >
        CREATED BY BILLY
      </Badge>
    </Group>
  )
}
